import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  IconButton,
  Button,
  CircularProgress,
  Stack,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { accountChipSx } from "../accountColors";
import { useUniboxColors } from "../../useUniboxColors";
import {
  canInlinePreview,
  downloadAttachmentFile,
  fetchAttachmentBlob,
  formatBytes,
  isImageFile,
  isPdfFile,
  openAttachmentInNewTab,
} from "./attachmentUtils";
import PdfCanvasPreview from "./PdfCanvasPreview";

export default function AttachmentPreviewModal({
  open,
  item,
  onClose,
  onOpenInConversation,
}) {
  const c = useUniboxColors();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewKind, setPreviewKind] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open || !item?.id) {
      setPreviewUrl(null);
      setPreviewKind(null);
      setError(false);
      return undefined;
    }

    let cancelled = false;
    let objectUrl = null;

    const load = async () => {
      setLoading(true);
      setError(false);
      setPreviewUrl(null);
      setPreviewKind(null);

      try {
        if (!canInlinePreview(item.filename, item.mimetype)) {
          setPreviewKind("unsupported");
          return;
        }

        if (isPdfFile(item.filename, item.mimetype)) {
          setPreviewKind("pdf");
          return;
        }

        const blob = await fetchAttachmentBlob(
          item.id,
          item.filename,
          item.mimetype
        );
        if (cancelled) return;

        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);

        if (isImageFile(item.filename, item.mimetype) || blob.type.startsWith("image/")) {
          setPreviewKind("image");
        } else {
          setPreviewKind("unsupported");
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [open, item?.id, item?.filename, item?.mimetype]);

  useEffect(
    () => () => {
      if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl]
  );

  const handleOpenPdfTab = () => {
    if (!item?.id) return;
    try {
      openAttachmentInNewTab(item.id);
    } catch (err) {
      window.alert(err?.message || "No se pudo abrir el PDF.");
    }
  };

  const handleDownload = async () => {
    if (!item?.id) return;
    try {
      await downloadAttachmentFile(item.id, item.filename);
    } catch (err) {
      window.alert(
        err?.message || "No se pudo descargar el archivo."
      );
    }
  };

  if (!item) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          bgcolor: c.card,
          border: `1px solid ${c.border}`,
          boxShadow: c.shadowMd,
          overflow: "hidden",
          maxHeight: "94vh",
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: `1px solid ${c.border}`,
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 16,
              letterSpacing: -0.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.filename}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: c.textMuted,
              mt: 0.35,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {item.subject || "(Sin asunto)"}
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: "wrap", gap: 0.5 }}>
            {item.accountEmail ? (
              <Chip
                label={item.accountEmail}
                size="small"
                sx={{
                  height: 22,
                  fontSize: 10.5,
                  border: "1px solid",
                  ...accountChipSx(item.accountEmail, {
                    metaBg: c.metaBg,
                    border: c.border,
                    textMuted: c.textMuted,
                  }),
                }}
              />
            ) : null}
            <Typography sx={{ fontSize: 11, color: c.textDim, alignSelf: "center" }}>
              {formatBytes(item.size)}
            </Typography>
          </Stack>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: c.textMuted }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          minHeight: 280,
          maxHeight: "68vh",
          overflow: "auto",
          bgcolor: c.isDark ? c.panel : "#f1f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        {loading ? (
          <CircularProgress size={32} sx={{ color: c.accent }} />
        ) : error ? (
          <Box sx={{ textAlign: "center", px: 2 }}>
            <Typography sx={{ fontWeight: 700, color: c.textPrimary }}>
              No se pudo cargar la vista previa
            </Typography>
            <Typography sx={{ fontSize: 12, color: c.textMuted, mt: 0.5 }}>
              Puedes descargar el archivo para abrirlo en tu equipo.
            </Typography>
          </Box>
        ) : previewKind === "image" && previewUrl ? (
          <Box
            component="img"
            src={previewUrl}
            alt={item.filename}
            sx={{
              maxWidth: "100%",
              maxHeight: "64vh",
              objectFit: "contain",
              borderRadius: "8px",
              boxShadow: c.shadowSm,
              bgcolor: "#fff",
            }}
          />
        ) : previewKind === "pdf" ? (
          <PdfCanvasPreview
            attachmentId={item.id}
            filename={item.filename}
            colors={c}
          />
        ) : (
          <Box sx={{ textAlign: "center", px: 3 }}>
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 56, color: c.textDim, mb: 1 }} />
            <Typography sx={{ fontWeight: 700, color: c.textPrimary }}>
              Vista previa no disponible
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: c.textMuted, mt: 0.5 }}>
              Este tipo de archivo no se puede previsualizar en el navegador.
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderTop: `1px solid ${c.border}`,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        {previewKind === "pdf" ? (
          <Button
            variant="outlined"
            startIcon={<OpenInNewOutlinedIcon />}
            onClick={handleOpenPdfTab}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: c.radius,
              borderColor: c.border,
            }}
          >
            Abrir en pestaña
          </Button>
        ) : null}
        {onOpenInConversation ? (
          <Button
            variant="outlined"
            startIcon={<OpenInNewOutlinedIcon />}
            onClick={() => {
              onClose();
              onOpenInConversation(item);
            }}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: c.radius,
              borderColor: c.border,
            }}
          >
            Abrir conversación
          </Button>
        ) : null}
        <Button
          variant="contained"
          startIcon={<DownloadOutlinedIcon />}
          onClick={handleDownload}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: c.radius,
            bgcolor: c.accent,
            "&:hover": { bgcolor: c.accentBorder },
          }}
        >
          Descargar
        </Button>
      </Box>
    </Dialog>
  );
}
